import { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import {
  adminMeetingReportCounts,
  adminMeetingReportExport,
  adminMeetingReportUtms,
} from "../../../config/service";
import YarpaUsageReport from "./YarpaUsageReport";

// Kept in step with MEETING_REPORT_SEGMENTS / MEETING_REPORT_STATUSES in the
// backend helper; the server rejects anything outside these lists.
const SEGMENTS = [
  { value: "idf", label: "IDF" },
  { value: "rx", label: "Rx" },
  { value: "private", label: "Private" },
  { value: "all", label: "All" },
];

const STATUSES = [
  "Created",
  "DatesOffered",
  "Scheduled",
  "Done",
  "Canceled",
  "AdminCare",
  "NoShow",
];

const STATUS_PRESETS = [
  { key: "done", label: "Done only", statuses: ["Done"] },
  { key: "missed", label: "No show + canceled", statuses: ["NoShow", "Canceled"] },
  { key: "all", label: "All statuses", statuses: STATUSES },
];

const DATE_FORMAT = "YYYY-MM-DD";
const MAX_RANGE_DAYS = 366;

// Matches MEETING_REPORT_NO_UTM in the backend helper: meetings with no UTM.
const NO_UTM = "(none)";

// The page renders inside an RTL body while Bootstrap's stylesheet is LTR, so
// the select's arrow would sit on top of right-aligned text. Move it left.
const RTL_SELECT_STYLE = {
  backgroundPosition: "left 0.75rem center",
  paddingLeft: "2.25rem",
  paddingRight: "0.75rem",
};

const lastMonthRange = () => ({
  startDate: moment().subtract(1, "month").startOf("month").toDate(),
  endDate: moment().subtract(1, "month").endOf("month").toDate(),
});

const sortedEntries = (counts) =>
  Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);

/** Reads the server's filename, falling back to a generic one. */
const filenameFromResponse = (response) => {
  const disposition = response?.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  return match ? match[1] : `meeting-report-${moment().format(DATE_FORMAT)}.csv`;
};

/** Error bodies arrive as an ArrayBuffer on the export call, so decode them. */
const errorMessageFrom = (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      return JSON.parse(new TextDecoder().decode(data))?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || error?.message || fallback;
};

const MeetingReports = () => {
  const initialRange = lastMonthRange();

  const [segment, setSegment] = useState("private");
  const [statuses, setStatuses] = useState(["Done"]);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [utm, setUtm] = useState("");
  const [utmOptions, setUtmOptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    adminMeetingReportUtms()
      .then((response) => setUtmOptions(response?.data || []))
      .catch(() => setUtmOptions([]));
  }, []);

  const rangeDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return (
      moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") + 1
    );
  }, [startDate, endDate]);

  const rangeError = useMemo(() => {
    if (!startDate || !endDate) return "Choose a start and end date.";
    if (rangeDays <= 0) return "The start date is after the end date.";
    if (rangeDays > MAX_RANGE_DAYS) {
      return "The range is longer than a year. Narrow it down.";
    }
    return "";
  }, [startDate, endDate, rangeDays]);

  const buildPayload = () => ({
    segment,
    // Every status ticked means the same as none: no status filter at all.
    statuses: statuses.length === STATUSES.length ? [] : statuses,
    startDay: moment(startDate).format(DATE_FORMAT),
    endDay: moment(endDate).format(DATE_FORMAT),
    utm,
  });

  const handleStatusToggle = (status) => {
    setStatuses((current) =>
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status],
    );
    setSummary(null);
  };

  const handlePreview = async () => {
    if (rangeError) return toast.error(rangeError);

    setIsLoading(true);
    try {
      const response = await adminMeetingReportCounts(buildPayload());
      setSummary(response?.data?.summary || null);
    } catch (error) {
      setSummary(null);
      toast.error(errorMessageFrom(error, "Could not load the report counts."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (rangeError) return toast.error(rangeError);

    setIsLoading(true);
    try {
      const response = await adminMeetingReportExport(buildPayload());
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "text/csv" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filenameFromResponse(response));
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errorMessageFrom(error, "Could not export the report."));
    } finally {
      setIsLoading(false);
    }
  };

  const renderCounts = (title, counts) => {
    const entries = sortedEntries(counts);
    if (entries.length === 0) return null;

    return (
      <div className="col-md-4 mb-3">
        <h6 className="text-uppercase text-muted">{title}</h6>
        <ul className="list-group list-group-flush">
          {entries.map(([label, count]) => (
            <li
              key={label}
              className="list-group-item d-flex justify-content-between px-0 py-1"
            >
              <span className="text-truncate pe-2">{label}</span>
              <strong>{count}</strong>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="user-mangement-page">
      {/* Same page shell as the other admin pages, with the controls held to a
          readable width instead of stretching across the screen. */}
      <div className="container-fluid py-4" style={{ maxWidth: 960 }}>
        <h4 className="mb-4">דוחות</h4>

        <div className="card mb-4">
          <div className="card-body p-4">
            <div className="mb-4">
              <label className="form-label fw-bold">Segment</label>
              <div className="d-flex flex-wrap gap-2">
                {SEGMENTS.map((option) => (
                  <Button
                    key={option.value}
                    variant={segment === option.value ? "dark" : "outline-dark"}
                    onClick={() => {
                      setSegment(option.value);
                      setSummary(null);
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Status</label>
              <div className="d-flex flex-wrap gap-4 mb-3">
                {/* Not Form.Check: its spacing is a left padding that lands on
                    the wrong side in an RTL body, pushing label against box. */}
                {STATUSES.map((status) => (
                  <label
                    key={status}
                    className="d-inline-flex align-items-center gap-2 mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      style={{ marginInlineStart: 0 }}
                      checked={statuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                    />
                    <span>{status}</span>
                  </label>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {STATUS_PRESETS.map((preset) => (
                  <Button
                    key={preset.key}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setStatuses(preset.statuses);
                      setSummary(null);
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">From</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setSummary(null);
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">To</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    setSummary(null);
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">UTM</label>
                <Form.Select
                  value={utm}
                  style={RTL_SELECT_STYLE}
                  onChange={(event) => {
                    setUtm(event.target.value);
                    setSummary(null);
                  }}
                >
                  <option value="">All UTMs</option>
                  <option value={NO_UTM}>No UTM</option>
                  {utmOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            {rangeError ? (
              <p className="text-danger mb-4">{rangeError}</p>
            ) : (
              <p className="text-muted mb-4">
                {rangeDays} days, by scheduled date, Israel time
              </p>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 pt-2">
              <Button
                variant="outline-dark"
                disabled={isLoading || Boolean(rangeError)}
                onClick={handlePreview}
              >
                Preview counts
              </Button>
              <Button
                className="download_report"
                disabled={isLoading || Boolean(rangeError)}
                onClick={handleExport}
              >
                Download CSV
              </Button>
              {isLoading && <RotatingLines width="28" strokeColor="grey" />}
            </div>
          </div>
        </div>

        {summary && (
          <div className="card mb-4">
            <div className="card-body p-4">
              <h5 className="mb-3">
                Total meetings: {summary.total}
                {summary.rxCount > 0 && (
                  <span className="text-muted fs-6">
                    {" "}
                    ({summary.rxCount} prescription meetings)
                  </span>
                )}
              </h5>
              <div className="row">
                {renderCounts("By status", summary.byStatus)}
                {renderCounts("By meeting group", summary.byMeetingGroup)}
                {renderCounts("By consulting field", summary.byConsultation)}
              </div>
            </div>
          </div>
        )}

        <YarpaUsageReport />
      </div>
    </div>
  );
};

export default MeetingReports;
